import React, { useEffect, useState } from "react";
import "./App.css";
import {
  APP_STORE_BADGE,
  GOOGLE_PLAY_BADGES,
  RUSTORE_BADGE,
} from "./storeBadges";

type Language = "en" | "ru";
type StoreId = "appstore" | "googleplay" | "rustore";

interface StoreLink {
  id: StoreId;
  eyebrow: string;
  label: string;
  href: string;
}

interface LocalizedContent {
  meta: {
    title: string;
    description: string;
  };
  brandLabel: string;
  heroEyebrow: string;
  heroTitle: string;
  heroText: string;
  heroPreviewAlt: string;
  heroHighlights: string[];
  heroCards: Array<{
    value: string;
    label: string;
  }>;
  downloadLabel: string;
  trustTitle: string;
  trustText: string;
  stores: StoreLink[];
  statsTitle: string;
  stats: Array<{
    value: string;
    label: string;
    note: string;
  }>;
  featuresTitle: string;
  featuresLead: string;
  features: Array<{
    title: string;
    text: string;
  }>;
  stepsTitle: string;
  stepsLead: string;
  steps: Array<{
    title: string;
    text: string;
  }>;
  galleryTitle: string;
  galleryLead: string;
  gallery: Array<{
    title: string;
    text: string;
    alt: string;
  }>;
  footerTitle: string;
  footerText: string;
  footerLinks: Array<{
    label: string;
    href: string;
  }>;
}

const STORAGE_KEY = "cleancy-landing-language";

const SCREENSHOTS: Record<Language, string[]> = {
  en: [
    `${process.env.PUBLIC_URL}/assets/screen-en-1.webp`,
    `${process.env.PUBLIC_URL}/assets/screen-en-2.webp`,
    `${process.env.PUBLIC_URL}/assets/screen-en-3.webp`,
    `${process.env.PUBLIC_URL}/assets/screen-en-4.webp`,
  ],
  ru: [
    `${process.env.PUBLIC_URL}/assets/screen-1.webp`,
    `${process.env.PUBLIC_URL}/assets/screen-2.webp`,
    `${process.env.PUBLIC_URL}/assets/screen-3.webp`,
    `${process.env.PUBLIC_URL}/assets/screen-4.webp`,
  ],
};

const HERO_VISUALS: Record<Language, string> = {
  en: `${process.env.PUBLIC_URL}/assets/hero-device.png`,
  ru: `${process.env.PUBLIC_URL}/assets/hero-device.png`,
};

function updateMetaTag(selector: string, content: string) {
  const element = document.querySelector<HTMLMetaElement>(selector);
  if (element) {
    element.setAttribute("content", content);
  }
}

const CONTENT: Record<Language, LocalizedContent> = {
  en: {
    meta: {
      title: "Clean Nature App | Report Dumpsites and Confirm Cleanups",
      description:
        "Clean Nature helps volunteers report dumpsites, find cleanup spots nearby, upload photo proof, and track the result on the map.",
    },
    brandLabel: "Clean Nature Center 12-15",
    heroEyebrow: "Clean Nature mobile app",
    heroTitle: "Report dumpsites. Clean nearby places. See the impact.",
    heroText:
      "Clean Nature helps volunteers and eco-activists record pollution, find nearby cleanup spots, upload photo reports, and earn points inside one mobile app.",
    heroPreviewAlt: "Clean Nature app map and reports screen",
    heroHighlights: [
      "iOS and Android",
      "English and Russian",
      "Leaderboard and eco store",
    ],
    heroCards: [
      {
        value: "400+",
        label: "pollution cases resolved in 2025 thanks to active users",
      },
      {
        value: "Map + photo proof",
        label: "a simple flow for reporting and confirming cleanup",
      },
    ],
    downloadLabel: "Download the app",
    trustTitle: "Built together with Clean Nature Center 12-15",
    trustText:
      "The app supports eco-events, volunteer cleanups, public reporting, and everyday local action with a clear, practical workflow.",
    stores: [
      {
        id: "appstore",
        eyebrow: "Download on the",
        label: "App Store",
        href: "https://apps.apple.com/us/app/clean-nature/id6504939725",
      },
      {
        id: "googleplay",
        eyebrow: "Get it on",
        label: "Google Play",
        href: "https://play.google.com/store/apps/details?id=com.cleancy&pli=1",
      },
      {
        id: "rustore",
        eyebrow: "Available in",
        label: "RuStore",
        href: "https://www.rustore.ru/catalog/app/com.cleancy",
      },
    ],
    statsTitle: "Real activity behind every report",
    stats: [
      {
        value: "400+",
        label: "pollution cases handled in 2025",
        note: "Mentioned in the app launch article.",
      },
      {
        value: "1,970 ha",
        label: "territories cleaned by the Center",
        note: "Current public results on clean-nature.org.",
      },
      {
        value: "131,371",
        label: "trees planted",
        note: "Organization impact figures for the current period.",
      },
    ],
    featuresTitle: "Everything needed for local environmental action",
    featuresLead:
      "The app stays focused on the essentials: report faster, find nearby points, clean them, and show the result.",
    features: [
      {
        title: "Report pollution in a few taps",
        text: "Take a photo, add a comment, choose the type and scale of pollution, and send the report from the map.",
      },
      {
        title: "See nearby points on the map",
        text: "Browse reports around you and choose locations that need attention right now.",
      },
      {
        title: "Upload cleanup proof",
        text: "After cleanup, publish a photo report and mark the location as cleaned so others can see the result.",
      },
      {
        title: "Earn points and recognition",
        text: "Move up the leaderboard and spend points in the in-app eco store on useful eco products.",
      },
    ],
    stepsTitle: "How it works",
    stepsLead:
      "The flow is deliberately short, so acting on a problem feels lighter than ignoring it.",
    steps: [
      {
        title: "Create your profile",
        text: "Register with email, choose a nickname, and join the eco-community in a few minutes.",
      },
      {
        title: "Report or clean",
        text: "Use the map to log a dumpsite or pick a nearby point that you can clean yourself.",
      },
      {
        title: "Share the result",
        text: "Upload photos, collect points, and take part in eco-marathons with prizes and public recognition.",
      },
    ],
    galleryTitle: "Inside the app",
    galleryLead:
      "Store screenshots show the core experience: a public map, structured reporting, and clear cleanup confirmation.",
    gallery: [
      {
        title: "Interactive map",
        text: "View reports and nearby cleanup points in one place.",
        alt: "Map screen from the Clean Nature app",
      },
      {
        title: "Structured reporting",
        text: "Add photos, pollution type, and a short comment without extra friction.",
        alt: "Report form screen from the Clean Nature app",
      },
      {
        title: "Cleanup flow",
        text: "Confirm the result with a photo report after the location is cleaned.",
        alt: "Cleanup confirmation screen from the Clean Nature app",
      },
      {
        title: "Points and progress",
        text: "Track your contribution through the rating and reward system.",
        alt: "Points and progress screen from the Clean Nature app",
      },
    ],
    footerTitle: "Keep Clean Nature close at hand",
    footerText:
      "Choose the store that fits your device and use the app whenever you spot pollution or join a cleanup nearby.",
    footerLinks: [
      {
        label: "Organization website",
        href: "https://clean-nature.org/",
      },
      {
        label: "App article",
        href: "https://clean-nature.org/blog/tpost/egll5t10d1-mobilnoe-prilozhenie-chistaya-priroda",
      },
    ],
  },
  ru: {
    meta: {
      title: "Чистая Природа | Сообщайте о свалках и подтверждайте уборку",
      description:
        "«Чистая Природа» помогает волонтёрам сообщать о свалках, находить точки уборки рядом, загружать фотоотчёты и видеть результат на карте.",
    },
    brandLabel: "Центр Чистой Природы 12-15",
    heroEyebrow: "Мобильное приложение «Чистая Природа»",
    heroTitle:
      "Сообщайте о загрязнении. Убирайте рядом. Показывайте результат.",
    heroText:
      "Приложение помогает волонтёрам и экоактивистам фиксировать загрязнения, находить точки на карте, загружать фотоотчёты об уборке и получать баллы за полезные действия.",
    heroPreviewAlt: "Экран карты и отчётов приложения «Чистая Природа»",
    heroHighlights: [
      "iOS и Android",
      "Русский и английский",
      "Рейтинг и экотовары",
    ],
    heroCards: [
      {
        value: "400+",
        label:
          "случаев загрязнения уже ликвидировано в 2025 году благодаря активности пользователей",
      },
      {
        value: "Карта + фотоотчёт",
        label:
          "простой сценарий для сообщения о проблеме и подтверждения уборки",
      },
    ],
    downloadLabel: "Скачать приложение",
    trustTitle: "Создано вместе с Центром Чистой Природы 12-15",
    trustText:
      "Приложение поддерживает экособытия, субботники, публичную карту загрязнений и повседневные локальные действия без лишней сложности.",
    stores: [
      {
        id: "appstore",
        eyebrow: "Скачать в",
        label: "App Store",
        href: "https://apps.apple.com/ru/app/%D1%87%D0%B8%D1%81%D1%82%D0%B0%D1%8F-%D0%BF%D1%80%D0%B8%D1%80%D0%BE%D0%B4%D0%B0/id6504939725",
      },
      {
        id: "googleplay",
        eyebrow: "Доступно в",
        label: "Google Play",
        href: "https://play.google.com/store/apps/details?id=com.cleancy&pli=1",
      },
      {
        id: "rustore",
        eyebrow: "Скачать в",
        label: "RuStore",
        href: "https://www.rustore.ru/catalog/app/com.cleancy",
      },
    ],
    statsTitle: "За каждым сообщением стоит реальная работа",
    stats: [
      {
        value: "400+",
        label: "случаев загрязнения устранено в 2025 году",
        note: "Указано в статье о запуске приложения.",
      },
      {
        value: "1 970 га",
        label: "территорий уже очищено Центром",
        note: "Публичные результаты на clean-nature.org.",
      },
      {
        value: "131 371",
        label: "высаженных деревьев",
        note: "Текущие показатели воздействия организации.",
      },
    ],
    featuresTitle: "Всё важное для локального экодействия",
    featuresLead:
      "Приложение сосредоточено на главном: быстро сообщить, найти точку рядом, убрать и показать, что задача решена.",
    features: [
      {
        title: "Сообщайте о загрязнении за пару минут",
        text: "Сделайте фото, добавьте комментарий, выберите тип и масштаб загрязнения и отправьте сообщение прямо с карты.",
      },
      {
        title: "Ищите точки рядом на карте",
        text: "Смотрите отчёты поблизости и выбирайте места, которым нужна уборка прямо сейчас.",
      },
      {
        title: "Загружайте подтверждение уборки",
        text: "После уборки опубликуйте фотоотчёт и отметьте точку как очищенную, чтобы результат был виден другим.",
      },
      {
        title: "Получайте баллы и признание",
        text: "Продвигайтесь в рейтинге и тратьте баллы во внутреннем магазине экотоваров.",
      },
    ],
    stepsTitle: "Как это работает",
    stepsLead:
      "Сценарий короткий и понятный, чтобы действовать было проще, чем откладывать.",
    steps: [
      {
        title: "Создайте профиль",
        text: "Зарегистрируйтесь по email, придумайте никнейм и присоединитесь к экосообществу за несколько минут.",
      },
      {
        title: "Сообщите или уберите",
        text: "Используйте карту, чтобы зафиксировать свалку или выбрать ближайшую точку для самостоятельной уборки.",
      },
      {
        title: "Покажите результат",
        text: "Загрузите фото, получите баллы и участвуйте в экомарафонах с призами и публичным признанием.",
      },
    ],
    galleryTitle: "Что внутри приложения",
    galleryLead:
      "Скриншоты из магазинов показывают ключевой опыт: публичную карту, структурированную отправку отчёта и понятное подтверждение уборки.",
    gallery: [
      {
        title: "Интерактивная карта",
        text: "Все точки загрязнения и уборки в одном интерфейсе.",
        alt: "Экран карты приложения «Чистая Природа»",
      },
      {
        title: "Понятная форма сообщения",
        text: "Фото, тип загрязнения и комментарий без лишних шагов.",
        alt: "Экран формы отчёта приложения «Чистая Природа»",
      },
      {
        title: "Подтверждение уборки",
        text: "После очистки территории легко загрузить фотоотчёт.",
        alt: "Экран подтверждения уборки приложения «Чистая Природа»",
      },
      {
        title: "Баллы и прогресс",
        text: "Собственная система рейтинга и мотивации для участников.",
        alt: "Экран баллов и прогресса приложения «Чистая Природа»",
      },
    ],
    footerTitle: "Пусть «Чистая Природа» будет под рукой",
    footerText:
      "Выберите магазин для своего устройства и открывайте приложение каждый раз, когда замечаете загрязнение или идёте на уборку рядом.",
    footerLinks: [
      {
        label: "Сайт организации",
        href: "https://clean-nature.org/",
      },
      {
        label: "Статья о приложении",
        href: "https://clean-nature.org/blog/tpost/egll5t10d1-mobilnoe-prilozhenie-chistaya-priroda",
      },
    ],
  },
};

function detectInitialLanguage(): Language {
  if (typeof window === "undefined") {
    return "ru";
  }

  const storedLanguage = window.localStorage.getItem(STORAGE_KEY);

  if (storedLanguage === "en" || storedLanguage === "ru") {
    return storedLanguage;
  }

  const browserLanguage = window.navigator.language?.toLowerCase() ?? "";

  if (browserLanguage.startsWith("en")) {
    return "en";
  }

  return "ru";
}

function getStoreBadgeSrc(store: StoreId, language: Language) {
  switch (store) {
    case "appstore":
      return APP_STORE_BADGE;
    case "googleplay":
      return GOOGLE_PLAY_BADGES[language];
    case "rustore":
      return RUSTORE_BADGE;
    default:
      return APP_STORE_BADGE;
  }
}

function StoreBadge({
  store,
  language,
}: {
  store: StoreLink;
  language: Language;
}) {
  return (
    <a
      className="store-badge"
      href={store.href}
      target="_blank"
      rel="noreferrer"
    >
      <img
        src={getStoreBadgeSrc(store.id, language)}
        alt={`${store.eyebrow} ${store.label}`}
      />
    </a>
  );
}

function App() {
  const [language, setLanguage] = useState<Language>(() =>
    detectInitialLanguage(),
  );
  const current = CONTENT[language];
  const screenshots = SCREENSHOTS[language];
  const heroVisual = HERO_VISUALS[language];

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
    document.title = current.meta.title;

    updateMetaTag('meta[name="description"]', current.meta.description);
    updateMetaTag('meta[property="og:title"]', current.meta.title);
    updateMetaTag('meta[property="og:description"]', current.meta.description);
    updateMetaTag('meta[name="twitter:title"]', current.meta.title);
    updateMetaTag('meta[name="twitter:description"]', current.meta.description);
    updateMetaTag(
      'meta[property="og:locale"]',
      language === "ru" ? "ru_RU" : "en_US",
    );
  }, [current.meta.description, current.meta.title, language]);

  return (
    <div className="landing-page">
      <header className="site-header">
        <a
          className="brand-lockup"
          href="https://clean-nature.org/"
          target="_blank"
          rel="noreferrer"
        >
          <img
            className="brand-icon"
            src={`${process.env.PUBLIC_URL}/assets/app-icon.png`}
            alt="Clean Nature app icon"
          />
          <div>
            <span className="brand-title">Clean Nature</span>
            <span className="brand-subtitle">{current.brandLabel}</span>
          </div>
        </a>

        <div className="language-toggle" aria-label="Language selector">
          {(["en", "ru"] as Language[]).map((item) => (
            <button
              key={item}
              className={item === language ? "is-active" : ""}
              onClick={() => setLanguage(item)}
              aria-pressed={item === language}
            >
              {item.toUpperCase()}
            </button>
          ))}
        </div>
      </header>

      <main>
        <section className="section hero-section">
          <div className="hero-copy">
            <p className="eyebrow">{current.heroEyebrow}</p>
            <h1>{current.heroTitle}</h1>
            <p className="hero-text">{current.heroText}</p>

            <div className="hero-highlights">
              {current.heroHighlights.map((item) => (
                <span key={item} className="highlight-chip">
                  {item}
                </span>
              ))}
            </div>

            <div className="download-panel">
              <p className="download-label">{current.downloadLabel}</p>
              <div className="store-grid">
                {current.stores.map((store) => (
                  <StoreBadge
                    key={store.id}
                    store={store}
                    language={language}
                  />
                ))}
              </div>
            </div>

            <div className="trust-block">
              <p className="trust-title">{current.trustTitle}</p>
              <p className="trust-text">{current.trustText}</p>
            </div>
          </div>

          <div className="hero-visual" aria-hidden="true">
            <div className="hero-orb hero-orb--lime" />
            <div className="hero-orb hero-orb--sky" />

            <div className="floating-card floating-card--top">
              <span className="floating-value">
                {current.heroCards[0].value}
              </span>
              <span className="floating-label">
                {current.heroCards[0].label}
              </span>
            </div>

            <div className="hero-device">
              <img
                src={heroVisual}
                alt={current.heroPreviewAlt}
                loading="eager"
              />
            </div>

            <div className="floating-card floating-card--bottom">
              <span className="floating-value">
                {current.heroCards[1].value}
              </span>
              <span className="floating-label">
                {current.heroCards[1].label}
              </span>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="section-heading compact-heading">
            <h2>{current.statsTitle}</h2>
          </div>
          <div className="stats-grid">
            {current.stats.map((item) => (
              <article key={item.label} className="stat-card">
                <p className="stat-value">{item.value}</p>
                <p className="stat-label">{item.label}</p>
                <p className="stat-note">{item.note}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-heading">
            <h2>{current.featuresTitle}</h2>
            <p>{current.featuresLead}</p>
          </div>

          <div className="feature-grid">
            {current.features.map((feature) => (
              <article key={feature.title} className="feature-card">
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section steps-section">
          <div className="section-heading">
            <h2>{current.stepsTitle}</h2>
            <p>{current.stepsLead}</p>
          </div>

          <div className="steps-grid">
            {current.steps.map((step, index) => (
              <article key={step.title} className="step-card">
                <span className="step-index">0{index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section-heading">
            <h2>{current.galleryTitle}</h2>
            <p>{current.galleryLead}</p>
          </div>

          <div className="gallery-grid">
            {current.gallery.map((item, index) => (
              <article key={item.title} className="gallery-card">
                <div className="gallery-shot">
                  <img src={screenshots[index]} alt={item.alt} loading="lazy" />
                </div>
                <div className="gallery-copy">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>

      <footer className="section footer-section">
        <div className="footer-card">
          <div className="footer-copy">
            <p className="eyebrow">Clean Nature</p>
            <h2>{current.footerTitle}</h2>
            <p>{current.footerText}</p>

            <div className="footer-links">
              {current.footerLinks.map((link) => (
                <a
                  key={link.href}
                  className="footer-link"
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>

          <div className="store-grid store-grid--compact">
            {current.stores.map((store) => (
              <StoreBadge
                key={`footer-${store.id}`}
                store={store}
                language={language}
              />
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
